import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Grid, 
  Card, 
  CardContent,
  Paper,
  Stack,
  Chip
} from '@mui/material';
import {
  Restaurant as RestaurantIcon,
  Inventory as InventoryIcon,
  MenuBook as MenuBookIcon,
  Schedule as ScheduleIcon,
  SmartToy as SmartToyIcon,
  EnergySavingsLeaf as EcoIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon
} from '@mui/icons-material';

export default function MarketingLandingPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: <InventoryIcon sx={{ fontSize: 60, color: '#2196f3' }} />,
      title: 'Smart Pantry Management',
      description: 'Track your ingredients with expiry date monitoring and intelligent notifications to reduce food waste.'
    },
    {
      icon: <SmartToyIcon sx={{ fontSize: 60, color: '#4caf50' }} />,
      title: 'AI-Powered Recipe Generation',
      description: 'Get personalized recipes based on your available ingredients and dietary preferences using advanced AI.'
    },
    {
      icon: <MenuBookIcon sx={{ fontSize: 60, color: '#ff9800' }} />,
      title: 'Personal Cookbook',
      description: 'Save your favorite recipes and build a personalized collection for easy access anytime.'
    },
    {
      icon: <EcoIcon sx={{ fontSize: 60, color: '#4caf50' }} />,
      title: 'Reduce Food Waste',
      description: 'Get smart suggestions to use expiring ingredients and minimize waste in your kitchen.'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Add Your Ingredients',
      description: 'Scan receipts or manually add ingredients to your virtual pantry'
    },
    {
      number: '2',
      title: 'Generate Recipes',
      description: 'Our AI suggests recipes based on what you have available'
    },
    {
      number: '3',
      title: 'Cook & Save',
      description: 'Cook amazing meals and save your favorites to your cookbook'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              mb: 3
            }}
          >
            Your AI-Powered Kitchen Assistant
          </Typography>
          <Typography
            variant="h5"
            sx={{
              mb: 4,
              opacity: 0.9,
              maxWidth: '800px',
              mx: 'auto',
              fontSize: { xs: '1.2rem', md: '1.5rem' }
            }}
          >
            Transform your cooking experience with smart pantry management, 
            AI recipe generation, and intelligent food waste reduction.
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            sx={{ mt: 4 }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                bgcolor: 'white',
                color: '#667eea',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  transform: 'translateY(-2px)',
                  boxShadow: 3
                }
              }}
            >
              Get Started Free
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/login')}
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Sign In
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ pt: 4, pb: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Why Choose Virtual Kitchen?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Everything you need to manage your kitchen smarter and cook better
          </Typography>
        </Box>

        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          justifyContent: 'center',
          alignItems: 'stretch'
        }}>
          {features.map((feature, index) => (
            <Card
              key={index}
              sx={{
                flex: { xs: 'none', md: '1 1 0' },
                width: { xs: '100%', md: 'auto' },
                maxWidth: { xs: '400px', md: 'none' },
                minHeight: { xs: 'auto', md: '280px' },
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
            >
              <CardContent
                sx={{
                  p: 3,
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                    {feature.title}
                  </Typography>
                </Box>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    lineHeight: 1.5,
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Container>

      {/* How It Works Section */}
      <Box sx={{ bgcolor: '#f8f9fa', py: 8 }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            component="h2" 
            gutterBottom 
            sx={{ 
              textAlign: 'center', 
              fontWeight: 'bold',
              mb: 6 
            }}
          >
            How It Works
          </Typography>
          
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 4,
            justifyContent: 'center',
            alignItems: 'stretch'
          }}>
            {steps.map((step, index) => (
              <Box 
                key={index}
                sx={{ 
                  flex: { xs: 'none', md: '1 1 0' },
                  textAlign: 'center',
                  maxWidth: { xs: '400px', md: 'none' },
                  mx: { xs: 'auto', md: 0 },
                  minHeight: { xs: 'auto', md: '200px' },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Chip
                    label={step.number}
                    color="primary"
                    sx={{
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      width: 48,
                      height: 48,
                      mb: 3
                    }}
                  />
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                    {step.title}
                  </Typography>
                </Box>
                <Typography 
                  variant="body1" 
                  color="text.secondary" 
                  sx={{ 
                    lineHeight: 1.6,
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {step.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Call to Action Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 8,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
            Ready to Transform Your Kitchen?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Join thousands of users who are already cooking smarter and wasting less food.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/register')}
            sx={{
              bgcolor: 'white',
              color: '#667eea',
              px: 5,
              py: 2,
              fontSize: '1.2rem',
              fontWeight: 'bold',
              '&:hover': {
                bgcolor: '#f5f5f5',
                transform: 'translateY(-2px)',
                boxShadow: 3
              }
            }}
          >
            Start Your Free Account
          </Button>
        </Container>
      </Box>
    </Box>
  );
}
